import cx from 'classnames';
import * as _ from 'lodash';
import { useCallback, useMemo, type VoidFunctionComponent } from 'react';
import type { Column, ComponentPropsGetterR, SortingRule } from 'react-table';

import Locator from '../../../../shared/Locator';
import { useParams, useQuery } from '../../../contexts/RouterContext';
import { useSnippets } from '../../../hooks/api/snippets';
import { IconAndNode, icons } from '../../generic';
import Drawer from '../../generic/Drawer';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../generic/Pagination/consts';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import SimpleReactTable from '../../generic/SimpleReactTable';
import TableHeader from '../../generic/TableHeader';
import type { GetSnippetsParams, Snippet, SnippetsQueryParams } from '../../../../shared/snippets';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import ConfidenceBadge from './ConfidenceBadge';
import LicenseIssueCounts from './LicenseIssueCounts';
import SearchBar from './SearchBar';
import SnippetsTreeView from './SnippetsTreeView';

import styles from './Snippets.module.less';

const defaultSortMode: SortingRule[] = [{
  id: 'package',
  desc: false,
}];

const snippetKindIcons = {
  snippet: icons.receipt,
  file: icons.fileOutline,
  dependency: icons.box,
};

const columns: Array<Column<Snippet>> = [
  {
    id: 'package',
    Header: <TableHeader title="Package" />,
    Cell: ({ original: snippet }: { original: Snippet }) => {
      // Snippets don't contain the raw package name so we need to parse it out of the Locator.
      const packageName = Locator.parse(snippet.id)?.package ?? 'Unknown';
      const icon = snippetKindIcons[snippet.kind];
      return (
        <div>
          <IconAndNode icon={icon} marginAmount="0.5em">
            <span>{packageName}</span>
          </IconAndNode>
        </div>
      );
    },
    resizable: false,
    sortable: false,
    // Ensure the text doesn't overflow and instead truncates with an ellipsis.
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'version',
    Header: <TableHeader title="Version" />,
    //  Snippets don't contain the raw package version, we need to parse them out of a Locator
    accessor: (row: Snippet) => {
      return Locator.parse(row.id)?.revision ?? '?.?.?';
    },
    resizable: false,
    sortable: false,
    // width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'issues',
    Header: <TableHeader title="License Issues" />,
    Cell: ({ original: snippet }: { original: Snippet }) => {
      return <LicenseIssueCounts issueCounts={snippet.issueCounts.licensing} />;
    },
    resizable: false,
    sortable: false,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'confidence',
    Header: <TableHeader title="Conf." />,
    Cell: ({ original: snippet }: { original: Snippet }) => {
      return <ConfidenceBadge confidence={snippet.confidence} />;
    },
    resizable: false,
    sortable: false,
    // width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'matches',
    Header: <TableHeader title="Matches" />,
    accessor: (row: Snippet) => row.matchIds.length,
    resizable: false,
    sortable: false,
    // width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
];

type ClientSideOnlyQueryParams = Exclude<keyof SnippetsQueryParams, keyof GetSnippetsParams>;

const clientSideParams: ClientSideOnlyQueryParams[] = ['selectedSnippetId'];

/**
 * SnippetsContainer - Route container component for the Snippet Scanning page within a project.
 */
const SnippetsContainer: VoidFunctionComponent = () => {
  //  Note: this will change (and probably be moved) but for now this pulls the dummy data so we can see it.
  const params = useParams<RevisionRouteParams>();
  const userRevisionLocator = `${params.project_id}$${params.revision_id}`;

  const [queryParams, setQuery] = useQuery<SnippetsQueryParams>();
  //  Remove any FE-only query params
  const getSnippetsQueryParams: GetSnippetsParams = useMemo(() => _.omit(queryParams, clientSideParams), [queryParams]);
  const { data: snippets, isLoading: isLoadingSnippets } = useSnippets(userRevisionLocator, getSnippetsQueryParams);

  //  This is how we apply a click event + styling to the row itself.
  const getTableRowProps = useCallback((_rowState, rowInfo: { original: Snippet }) => ({
    className: cx(styles.tableRow, {
      [styles.tableRowActive]: rowInfo && queryParams?.selectedSnippetId === rowInfo?.original?.id,
    }),
    onClick: () => setQuery({ ...queryParams, selectedSnippetId: rowInfo?.original?.id }),
  }), [queryParams, setQuery]) as ComponentPropsGetterR;

  //  Unset the queryParam.drawerId to close it
  const onCloseDrawer = useCallback(() => {
    setQuery({ ...queryParams, selectedSnippetId: undefined });
  }, [queryParams, setQuery]);

  return (
    <>
      <h1 className={styles.heading}>
        Snippets
      </h1>
      <div className={styles.subheading}>
        <div className={styles.filters}>
          <SearchBar
            initialSearch={queryParams.search ?? ''}
            onSearchChange={(value) => {
              if (value) {
                setQuery({ ...queryParams, search: value });
              } else {
                // If the search is cleared, we want to remove the search from the params.
                setQuery(_.omit(queryParams, 'search'));
              }
            }}
          />
          {/* TODO(CORE-5351): Add confidence multi-select dropdown */}
          {/* TODO(CORE-5353): Add license status multi-select dropdown */}
        </div>
        <div>
          {/* TODO: Add compare button */}
          {/* TODO: Add actions dropdown menu */}
        </div>
      </div>
      <div className={styles.layout}>
        <SnippetsTreeView
          userRevisionLocator={userRevisionLocator}
          selectedPath={queryParams.path ?? '/'}
          setSelectedPath={(path) => setQuery({ ...queryParams, path })}
        />
        <div className={styles.table}>
          <SimpleReactTable
            data={snippets?.results ?? []}
            columns={columns}
            isLoading={isLoadingSnippets}
            defaultSortMode={defaultSortMode}
            minRows={10}
            //  TODO: this will likely change in the future.
            isSortable={false}
            getTrProps={getTableRowProps}
          />
        </div>
      </div>
      <PaginationFooter
        page={Number(queryParams.page) || DEFAULT_PAGE}
        count={Number(queryParams.pageSize) || DEFAULT_PAGE_SIZE}
        totalItems={snippets?.totalCount ?? 0}
        setPage={((page) => setQuery({ ...queryParams, page }))}
        setCount={((pageSize) => setQuery({ ...queryParams, pageSize }))}
      />
      <Drawer
        isOpen={!!queryParams.selectedSnippetId}
        onClose={onCloseDrawer}
      >
        {queryParams.selectedSnippetId}
      </Drawer>
    </>
  );
};

export default SnippetsContainer;
